import React, { useState, useCallback, useMemo } from 'react'
import PropTypes from 'prop-types'
import moment from 'moment'
import { useDispatch } from 'react-redux'
import injectStyles from 'react-jss'
import { Grid } from '@material-ui/core'
import { Row, Col } from 'react-bootstrap'
import SubmitButton from 'Components/Form/SubmitButton'
import { Input } from 'Components/Form/Input'
import { SelectDashboard } from 'Components/Form/SelectDashboard'
import { Checkbox } from 'Components/Form/Checkbox'
import { authorization } from '../../../../../Module/Auth/Auth'
import {
  AMBASSADOR_STATUS_KEY,
  AMBASSADOR_STATUSES,
  BLOCK_AMBASSADOR_REASON_OPTIONS,
  BLOCK_AMBASSADOR_REASON_OTHER,
} from 'Reducers/Ambassador/const'
import GenericFlatButton from '../../../../../Components/GenericFlatButton'
import {
  MISSION_STATUS_ENUM,
  STATUS_DOCUMENT_PENDING,
  STATUS_DOCUMENT_REJECTED,
} from '../../../../../shared/constants'
import {
  hideReduxModal,
  resetReduxModal,
  showReduxModal,
} from '../../../../../Reducers/UI/actions'
import { USER_PROFILE_LOGS_TYPE } from '../../../../../constants'
import OnSiteTrainingModal from '../../Ambasadors/AmbassadorsPipelineView/components/OnSiteTrainingModal'
import SymbolsCounter from '../../../../../Components/SymbolsCounter'
import { createUserProfileLog } from '../gql'
import HistoryLogTable from './HistoryLogTable'
import MigrateModal from '../MigrateModal'

const styles = {
  blockingDisclaimer: { margin: '16px 0' },
  checkboxWrapper: { display: 'flex' },
  checkboxLabel: { marginLeft: 16 },
  ModalOnsiteTrainingToPreAccess: {
    width: '50%',
    marginTop: '20px',
  },
}

const AmbassadorStatusChange = ({
  ambassador,
  timezone,
  onChange,
  onRefuse,
  onFadeChange,
  fade,
  documents,
  nmbrsDocument,
  disabled,
  countryCode,
  germanyContract,
  germanyPayroll,
  classes,
  missions,
}) => {
  const [blockReasonOption, setBlockReasonOption] = useState('')
  const [blockCustomText, setBlockCustomText] = useState('')
  const [showDisableForm, setShowDisableForm] = useState(false)
  const [forceUnbook, setForceUnbook] = useState(false)
  const [disablingBlock, setDisablingBlock] = useState(false)
  const [
    waitPreAccessMovePageReload,
    setWaitPreAccessMovePageReload,
  ] = useState(false)
  const [isMigrateModalOpen, setIsMigrateModalOpen] = useState(false)
  const { status, id } = ambassador
  const user = authorization.getUser()

  const bookedMissions = useMemo(
    () =>
      missions.filter(mission => mission.status === MISSION_STATUS_ENUM.BOOKED),
    [missions],
  )

  const hasBookedMissions = bookedMissions.length > 0

  const logsParams = type => ({
    input: { profile_user_id: +id, actioned_by_user_id: +user.id, type },
  })

  const handleBlockFormSubmit = useCallback(
    async e => {
      setDisablingBlock(true)
      const reason =
        blockReasonOption === BLOCK_AMBASSADOR_REASON_OTHER
          ? blockCustomText
          : blockReasonOption

      onChange({
        status: AMBASSADOR_STATUSES.blocked,
        blockedReason: reason,
        unbookMissions: forceUnbook,
      })

      //logs for block && unbook all missions in case that is true forceunbook
      await createUserProfileLog(logsParams(USER_PROFILE_LOGS_TYPE.BLOCK))
      forceUnbook &&
        (await createUserProfileLog(
          logsParams(USER_PROFILE_LOGS_TYPE.UNBOOK_ALL_MISSIONS),
        ))
      //set default state after submit
      setShowDisableForm(false)
      setBlockReasonOption('')
      setBlockCustomText('')
      setForceUnbook(false)
      setDisablingBlock(false)
    },
    [
      blockCustomText,
      blockReasonOption,
      forceUnbook,
      onChange,
      setShowDisableForm,
      setBlockReasonOption,
      setBlockCustomText,
      setForceUnbook,
    ],
  )

  const handleBlockReasonOptionChange = useCallback(
    value => setBlockReasonOption(value),
    [setBlockReasonOption],
  )
  const handleBlockCustomTextChange = useCallback(
    e => setBlockCustomText(e.target.value),
    [setBlockCustomText],
  )

  const handleApprove = useCallback(async () => {
    onChange({ status: AMBASSADOR_STATUSES.active })
    await createUserProfileLog(logsParams(USER_PROFILE_LOGS_TYPE.APPROVE))
  }, [onChange])

  const onApprove = () => {
    const notApprovedOrNotDownloadedDocs = documents.filter(
      doc =>
        (doc &&
          [STATUS_DOCUMENT_REJECTED, STATUS_DOCUMENT_PENDING].includes(
            doc.status,
          )) ||
        !doc.status,
    )

    const hasNmbrsDocProblems =
      !nmbrsDocument ||
      (nmbrsDocument && !nmbrsDocument.status) ||
      [STATUS_DOCUMENT_REJECTED, STATUS_DOCUMENT_PENDING].includes(
        nmbrsDocument.status,
      )
    const haveProblemsWithDocs =
      countryCode == 'NL'
        ? notApprovedOrNotDownloadedDocs.length > 0 || hasNmbrsDocProblems
        : notApprovedOrNotDownloadedDocs.length > 0

    if (haveProblemsWithDocs) {
      return dispatch(
        showReduxModal({
          title: 'Warning',
          children:
            'This BA has one or more rejected/missing signup files. Are you sure you want to approve them?',
          onConfirm: () => handleApprove(),
          onlyClickOnce: true,
        }),
      )
    }
    return handleApprove()
  }

  const handleShowDisableForm = () => setShowDisableForm(true)

  const toggleForceUnbook = () => setForceUnbook(!forceUnbook)

  const options = BLOCK_AMBASSADOR_REASON_OPTIONS.map(option => ({
    label: option,
    value: option,
  }))

  const handleUnbookMissions = useCallback(async () => {
    onChange({ unbookMissions: true })
    await createUserProfileLog(
      logsParams(USER_PROFILE_LOGS_TYPE.UNBOOK_ALL_MISSIONS),
    )
  }, [onChange])

  const isSubmitBlockedFormDisabled =
    blockReasonOption.length === 0 ||
    (blockReasonOption === BLOCK_AMBASSADOR_REASON_OTHER &&
      blockCustomText.length === 0)

  const generateButtons = ({ documents, nmbrsDocument }) => {
    let buttons = []
    const allDocuments = [...documents, nmbrsDocument, germanyContract, germanyPayroll]

    const rejectedDocuments = allDocuments
      ? allDocuments.filter(
          doc => doc && doc.status == STATUS_DOCUMENT_REJECTED,
        )
      : null
    const refuseButtonIsDisabled =
      rejectedDocuments && rejectedDocuments.length >= 0 ? false : true
    if (
      [
        AMBASSADOR_STATUS_KEY.BLOCKED,
        AMBASSADOR_STATUS_KEY.TERMINATING,
        AMBASSADOR_STATUS_KEY.BOOKED_MEETING,
        AMBASSADOR_STATUS_KEY.PENDING,
        AMBASSADOR_STATUS_KEY.WAITING_FOR_REVIEW,
      ].includes(status)
    ) {
      buttons.push(
        <GenericFlatButton
          type={'filled'}
          title={'Approve'}
          onClick={onApprove}
          key={AMBASSADOR_STATUSES.active}
          disabled={disabled}
        />,
      )
    }

    if (status && status !== AMBASSADOR_STATUS_KEY.BLOCKED) {
      buttons.push(
        <GenericFlatButton
          type={'outline'}
          title={'Block'}
          onClick={handleShowDisableForm}
          key={AMBASSADOR_STATUSES.blocked}
          disabled={disabled || showDisableForm}
        />,
      )
    }

    if ([AMBASSADOR_STATUS_KEY.WAITING_FOR_REVIEW].includes(status)) {
      buttons.push(
        <GenericFlatButton
          type={'outline'}
          title={'Refuse - send to Pre-Access'}
          onClick={() => {
            showRefuseModal()
            createUserProfileLog(
              logsParams(USER_PROFILE_LOGS_TYPE.REFUSE_SEND_TO_PRE_ACCESS),
            )
          }}
          key={AMBASSADOR_STATUSES.blocked}
          disabled={refuseButtonIsDisabled}
        />,
      )
    }

    if ([AMBASSADOR_STATUS_KEY.ON_SITE_TRAINING].includes(status)) {
      buttons.push(
        <GenericFlatButton
          type={'outline'}
          title={'Move to Pre Access'}
          onClick={handleModalOnsiteTrainig}
          disabled={waitPreAccessMovePageReload}
          key={AMBASSADOR_STATUSES.pre_access}
        />,
      )
    }

    if (
      [
        AMBASSADOR_STATUS_KEY.BLOCKED,
        AMBASSADOR_STATUS_KEY.ACTIVE,
        AMBASSADOR_STATUS_KEY.TERMINATING,
      ].includes(status)
    ) {
      buttons.push(
        <GenericFlatButton
          type={'outline'}
          title={'Unbook all missions'}
          onClick={showUnbookMissionsModal}
          key="unbookMissions"
          disabled={disabled || !hasBookedMissions}
          cypress_id={'unbookMissionsBtn'}
        />,
      )
    }

    if (
      [AMBASSADOR_STATUS_KEY.ACTIVE].includes(status) &&
      (authorization.isAdmin() || authorization.isCityManager())
    ) {
      buttons.push(
        <GenericFlatButton
          type={'outline'}
          title={fade ? 'Disable shadow ban' : 'Shadow ban'}
          onClick={() => {
            if (fade) {
              onFadeChange()
              createUserProfileLog(
                logsParams(USER_PROFILE_LOGS_TYPE.DISABLED_SHADOW_BAN),
              )
            } else {
              showShadowBanModal()
            }
          }}
          key="gradualFade"
          disabled={disabled}
          cypress_id={fade ? 'unfadeBtn' : 'gradualFadeBtn'}
        />,
      )
    }

    if (authorization.isAdmin() || authorization.isCityManager()) {
      buttons.push(
        <GenericFlatButton
          type={'outline'}
          title={'Migrate'}
          onClick={() => setIsMigrateModalOpen(true)}
          key="migrate"
          disabled={disabled}
          cypress_id={'migrateBtn'}
        />,
      )
    }

    buttons.push(
      <GenericFlatButton
        type={'outline'}
        title={'History Log'}
        onClick={showHistoryLogModal}
        cypress_id={'historyLogBtn'}
      />,
    )

    return buttons.map(button => <Grid item>{button}</Grid>)
  }

  const dispatch = useDispatch()

  const handleModalOnsiteTrainig = () => {
    setWaitPreAccessMovePageReload(true)
    setShowDisableForm(false)

    const onCancel = () => {
      setWaitPreAccessMovePageReload(false)
      dispatch(hideReduxModal())
      setTimeout(() => dispatch(resetReduxModal()), 100)
    }

    const onSubmit = data => {
      onChange(data)
      onCancel()
    }

    dispatch(
      showReduxModal({
        title: 'BA Onsite training completion rating',
        titleFontSize: '22px',
        hideButtons: true,
        aspect: 'nonScrollable',
        children: (
          <OnSiteTrainingModal
            onSubmit={onSubmit}
            onCancel={onCancel}
            ratedByUserId={user.id}
            userRated={id}
            isStatus={true}
          />
        ),
      }),
    )
  }

  const showHistoryLogModal = () =>
    dispatch(
      showReduxModal({
        title: 'History Log',
        children: (
          <HistoryLogTable
            ambassadorId={ambassador.id}
            hideModal={() => dispatch(hideReduxModal())}
          />
        ),
        isReportModal: true,
        hideButtons: true,
        aspect: 'conversions',
      }),
    )

  const showRefuseModal = () =>
    dispatch(
      showReduxModal({
        title: 'Warning',
        children:
          'Please confirm if you want this BA to be moved back to Pre-Access stage?',
        onConfirm: async () => await onRefuse(),
      }),
    )

  const showShadowBanModal = () =>
    dispatch(
      showReduxModal({
        title: 'Do you really want to shadow ban this user?',
        children:
          'The user will remain in Active status and be able to access the app as normal, but will never see any missions on the Browse Missions page.',
        onConfirm: () => {
          onFadeChange()
          createUserProfileLog(logsParams(USER_PROFILE_LOGS_TYPE.SHADOW_BAN))
        },
        cancelVariant: 'outlined',
      }),
    )

  const showUnbookMissionsModal = () =>
    dispatch(
      showReduxModal({
        title: 'Do you really want to unbook all of them?',
        children: (
          <div className="text-center">
            {`BA has ${bookedMissions.length} booked missions.`}
          </div>
        ),
        onConfirm: () => {
          handleUnbookMissions()
        },
        cancelVariant: 'outlined',
      }),
    )

  const symbolLimit = '100'
  return (
    <React.Fragment>
      <Grid container spacing={3}>
        {generateButtons({ documents, nmbrsDocument })}
      </Grid>
      {fade && (
        <div className="shadow-ban-text">
          Shadow banned on{' '}
          {moment.tz(fade, timezone).format('DD MMM YYYY, hh:mm')}
        </div>
      )}
      {showDisableForm && (
        <React.Fragment>
          <Row className={classes.blockingDisclaimer}>
            <Col xs={12}>
              <p>
                The brand ambassador will be able to complete their remaining
                booked missions before being blocked
              </p>
            </Col>
          </Row>
          <Row>
            <Col xs={12}>
              <SelectDashboard
                label="Reason:"
                input={{
                  name: 'unbookMission',
                  value: blockReasonOption,
                  onChange: handleBlockReasonOptionChange,
                }}
                options={options}
              />
            </Col>
          </Row>
          {blockReasonOption === BLOCK_AMBASSADOR_REASON_OTHER && (
            <Row className="buttons">
              <Col xs={12}>
                <Input
                  label="Specific reason:"
                  input={{
                    value: blockCustomText,
                    onChange: handleBlockCustomTextChange,
                  }}
                  meta={{}}
                  maxLength={symbolLimit}
                />
                <SymbolsCounter
                  valueToCounter={blockCustomText}
                  symbolLimit={symbolLimit}
                />
              </Col>
            </Row>
          )}
          <Row className="buttons">
            <Col xs={12} className={classes.checkboxWrapper}>
              <Checkbox
                input={{
                  onClick: toggleForceUnbook,
                  checked: forceUnbook,
                }}
              />
              <p className={classes.checkboxLabel}>
                Unbook and cancel all missions
              </p>
            </Col>
          </Row>
          <Row className="buttons">
            <Col xs={3}>
              <SubmitButton
                value="submit"
                disabled={
                  disabled || isSubmitBlockedFormDisabled || disablingBlock
                }
                onClick={handleBlockFormSubmit}
              />
            </Col>
          </Row>
        </React.Fragment>
      )}
      <MigrateModal
        userId={id}
        ambassadorName={ambassador.firstName ? `${ambassador.firstName} ${ambassador.lastName}` : null}
        isOpen={isMigrateModalOpen}
        onClose={() => setIsMigrateModalOpen(false)}
        onSuccess={() => {
          setIsMigrateModalOpen(false)
          window.location.reload()
        }}
      />
    </React.Fragment>
  )
}

AmbassadorStatusChange.propTypes = {
  classes: PropTypes.object.isRequired,
  ambassadorStatus: PropTypes.object.isRequired,
  onChange: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  fade: PropTypes.any,
  onFadeChange: PropTypes.func.isRequired,
  missions: PropTypes.array,
}

export default injectStyles(styles)(AmbassadorStatusChange)
